import { defineMessages, MessageDescriptor } from 'react-intl';
import type { OnboardingItemKey } from 'fym-shared/src/music24/onboardingChecklist';

/** The copy of the "Your first release" checklist (design: onboarding d1-d7). */
export const checklistMessages = defineMessages({
  title: { id: 'app.sml.firstRelease.title', defaultMessage: 'Your first release' },
  sub: { id: 'app.sml.firstRelease.sub', defaultMessage: '{song} · {when} · {done} of {total} done' },
  subNoSong: { id: 'app.sml.firstRelease.subNoSong', defaultMessage: '{done} of {total} done' },
  // QA 2026-10-28 (imp-7): "Next · 3 of 10" next to "4 of 10 done" read as two progress counts.
  next: { id: 'app.sml.firstRelease.nextStep', defaultMessage: 'Next step' },
  dismiss: { id: 'app.sml.firstRelease.dismiss', defaultMessage: 'Dismiss' },
  progress: { id: 'app.sml.firstRelease.progress', defaultMessage: '{done}/{total}' },
  trialLine: {
    id: 'app.sml.firstRelease.planPick.trialLine',
    defaultMessage: 'Trial ends in {days, plural, one {# day} other {# days}}. Then Free: the previews stay.',
  },
  done: {
    id: 'app.sml.firstRelease.done',
    defaultMessage:
      'Your first release is set. {song} has its kit, its plan and its link. Next song? Add it in Releases.',
  },
  doneNoSong: {
    id: 'app.sml.firstRelease.doneNoSong',
    defaultMessage: 'Your first release is set. Next song? Add it in Releases.',
  },
  hide: { id: 'app.sml.firstRelease.hide', defaultMessage: 'Hide' },
  pill: { id: 'app.sml.firstRelease.pill', defaultMessage: 'Your first release' },
  // design: navigation-v2 6d. The sidebar row.
  row: { id: 'app.sml.firstRelease.row', defaultMessage: 'First release' },
  rowHide: { id: 'app.sml.firstRelease.row.hide', defaultMessage: 'Hide the checklist' },
  rowDone: { id: 'app.sml.firstRelease.row.done', defaultMessage: 'First release set' },
  pillNext: { id: 'app.sml.firstRelease.pill.next', defaultMessage: '{done}/{total} · next: {item}' },
  setupHidden: {
    id: 'app.sml.firstRelease.setup.hidden',
    defaultMessage: 'Hidden on Home. The steps still count here.',
  },
  setupShow: { id: 'app.sml.firstRelease.setup.show', defaultMessage: 'Show the checklist again' },
  setupMemberNote: {
    id: 'app.sml.firstRelease.setup.memberNote',
    defaultMessage: 'You can do these steps too. Only the workspace owner picks a plan and hides this list on Home.',
  },
  setupOwnerOnly: {
    id: 'app.sml.firstRelease.setup.ownerOnly',
    defaultMessage: 'Only the workspace owner picks the plan.',
  },
  setupDemoTitle: {
    id: 'app.sml.firstRelease.setup.demo.title',
    defaultMessage: '{artist} is an example artist',
  },
  setupDemoBody: {
    id: 'app.sml.firstRelease.setup.demo.body',
    defaultMessage: 'Its songs and numbers show how MUSIC24 works. Your own setup is below.',
  },
  setupDemoBodyNoArtist: {
    id: 'app.sml.firstRelease.setup.demo.bodyNoArtist',
    defaultMessage: 'Its songs and numbers show how MUSIC24 works. Add your own artist to start your setup.',
  },
  setupDemoSwitch: { id: 'app.sml.firstRelease.setup.demo.switch', defaultMessage: 'Switch to {artist}' },
  setupAddArtist: { id: 'app.sml.firstRelease.setup.addArtist', defaultMessage: 'Add your artist' },
  setupNoArtist: {
    id: 'app.sml.firstRelease.setup.noArtist',
    defaultMessage: 'Add an artist to start your first release.',
  },
  itemDone: { id: 'app.sml.firstRelease.itemDone', defaultMessage: 'Done' },
  nudgeCover: { id: 'app.sml.firstRelease.nudge.cover', defaultMessage: 'Add a cover' },
  nudgeCoverBody: {
    id: 'app.sml.firstRelease.nudge.coverBody',
    defaultMessage: 'No cover yet. Your posts, videos and link look better with one.',
  },
});

export interface ChecklistItemCopy {
  title: MessageDescriptor;
  body: MessageDescriptor;
  action: MessageDescriptor;
}

const song = defineMessages({
  title: { id: 'app.sml.firstRelease.item.song.title', defaultMessage: 'Add your song' },
  body: { id: 'app.sml.firstRelease.item.song.body', defaultMessage: 'One song is all it takes to start.' },
  action: { id: 'app.sml.firstRelease.item.song.action', defaultMessage: 'Add my song' },
});

const audio = defineMessages({
  title: { id: 'app.sml.firstRelease.item.audio.title', defaultMessage: 'Add the audio' },
  body: {
    id: 'app.sml.firstRelease.item.audio.body',
    defaultMessage: 'Unlocks the videos: reels, lyric moments and more.',
  },
  action: { id: 'app.sml.firstRelease.item.audio.action', defaultMessage: 'Add audio' },
});

const lyrics = defineMessages({
  title: { id: 'app.sml.firstRelease.item.lyrics.title', defaultMessage: 'Add the lyrics' },
  body: { id: 'app.sml.firstRelease.item.lyrics.body', defaultMessage: 'Unlocks lyric cards and the lyric video.' },
  action: { id: 'app.sml.firstRelease.item.lyrics.action', defaultMessage: 'Add lyrics' },
});

const look = defineMessages({
  title: { id: 'app.sml.firstRelease.item.look.title', defaultMessage: 'Pick the look' },
  body: {
    id: 'app.sml.firstRelease.item.look.body',
    defaultMessage: 'One look for every post and video, made from your cover.',
  },
  action: { id: 'app.sml.firstRelease.item.look.action', defaultMessage: 'Pick a look' },
});

const kit = defineMessages({
  title: { id: 'app.sml.firstRelease.item.kit.title', defaultMessage: 'Get your kit' },
  body: {
    id: 'app.sml.firstRelease.item.kit.body',
    defaultMessage: 'Every story, post and cover size, ready to post.',
  },
  action: { id: 'app.sml.firstRelease.item.kit.action', defaultMessage: 'Make the kit' },
});

const channel = defineMessages({
  title: { id: 'app.sml.firstRelease.item.channel.title', defaultMessage: 'Connect a channel' },
  body: {
    id: 'app.sml.firstRelease.item.channel.body',
    defaultMessage: 'So MUSIC24 can post for you on TikTok, Instagram, YouTube, Facebook, Threads or X.',
  },
  action: { id: 'app.sml.firstRelease.item.channel.action', defaultMessage: 'Connect a channel' },
});

const plan = defineMessages({
  title: { id: 'app.sml.firstRelease.item.plan.title', defaultMessage: 'Put the plan on the calendar' },
  body: {
    id: 'app.sml.firstRelease.item.plan.body',
    defaultMessage: 'Every post at the right time, from the first teaser to six weeks after release day.',
  },
  action: { id: 'app.sml.firstRelease.item.plan.action', defaultMessage: 'Open the plan' },
});

const link = defineMessages({
  title: { id: 'app.sml.firstRelease.item.link.title', defaultMessage: 'Publish your link' },
  body: {
    id: 'app.sml.firstRelease.item.link.body',
    defaultMessage: 'One link with every service. Fans pre-save before release day.',
  },
  action: { id: 'app.sml.firstRelease.item.link.action', defaultMessage: 'Open the link' },
});

const streams = defineMessages({
  title: { id: 'app.sml.firstRelease.item.streams.title', defaultMessage: 'See your streams' },
  body: {
    id: 'app.sml.firstRelease.item.streams.body',
    defaultMessage: 'Connect Spotify for Artists or Ditto, and see what each post brought.',
  },
  action: { id: 'app.sml.firstRelease.item.streams.action', defaultMessage: 'Connect' },
});

const planPick = defineMessages({
  title: { id: 'app.sml.firstRelease.item.planPick.title', defaultMessage: 'Keep the full files' },
  body: {
    id: 'app.sml.firstRelease.item.planPick.body',
    defaultMessage: 'Keep the full files of your {count, plural, one {# asset} other {# assets}}.',
  },
  action: { id: 'app.sml.firstRelease.item.planPick.action', defaultMessage: 'See plans' },
});

/** The title, body and button of each item. The `planPick` body takes `{ count }`. */
export const checklistItemMessages: Record<OnboardingItemKey, ChecklistItemCopy> = {
  song,
  audio,
  lyrics,
  look,
  kit,
  channel,
  plan,
  link,
  streams,
  planPick,
};
